import { styled } from '@mui/material/styles';
import { Fragment } from 'react';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import { useInView } from 'react-intersection-observer';
import { Box } from '@mui/system';
import { Typography, Button, Link, useMediaQuery, Card } from '@mui/material';
import Marquee from "react-fast-marquee";
import Browser, { Chrome } from 'react-browser-ui'
import { Link as RRLink } from "react-router-dom";
import PieCard from "../components/Chart/PieCard";

const cardColors = ["#b54226", "#f97266", "#4686a8", "#105b72"];
const { Tab } = Chrome;

const Section = styled(({ itemRef, ...rest }) => (
    <Box component="section" ref={itemRef} {...rest} />
))(({ theme }) => ({
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    padding: "0 4vw",
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(10),
    [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(8),
    },
    [theme.breakpoints.up("lg")]: {
        marginBottom: theme.spacing(12),
    },
    [theme.breakpoints.up("xl")]: {
        marginTop: theme.spacing(11),
        marginBottom: theme.spacing(12),
    },
}));

const LazySection = ({ children, sx }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
    });

    return (
        <Section itemRef={ref} sx={sx}>
            {inView ? children : <div style={{ width: 1, height: 500 }} />}
        </Section>
    );
};

const SectionColumn = styled(Box)(
    ({ theme }) => ({
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        justifyContent: "space-evenly",
        [theme.breakpoints.up("md")]: {
            width: "48%",
        },
    })
);

const SectionTitle = styled((props) => (
    <Typography variant="h2" {...props} />
))(({ theme }) => ({
    fontWeight: 700,
    fontFamily: "Roboto Condensed",
    fontSize: "3.7rem",
    margin: theme.spacing(0.5, 0, 3, 0),
    lineHeight: 1.0625,
    [theme.breakpoints.up("sm")]: {
        fontSize: "5.5vw"
    },
    [theme.breakpoints.up("md")]: {
        fontSize: "3.7vw",
    },
}));

const SectionBody = styled((props) => (
    <Typography variant="h5" {...props} />
))(({ theme }) => ({
    fontWeight: 500,
    fontFamily: "inter",
    fontSize: "1.3rem",
    lineHeight: "1.44",
    color: "#4a4a4a",
    [theme.breakpoints.up("sm")]: {
        fontSize: "1.4rem",
    },
    [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        marginBottom: 0,
    },
    [theme.breakpoints.up("lg")]: {
        fontSize: "1.6rem",
    },
}));

const LogoMarquee = styled((props) => (
    <Marquee gradientWidth={30} speed={16} {...props} />
))(({ theme }) => ({
    margin: theme.spacing(3, 0, 0, 0),
    position: "relative",
    [theme.breakpoints.up("xl")]: {
        margin: theme.spacing(5, 0, 0, 0),
    },
}));

const LogoLink = styled(Link)(
    ({ theme }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: theme.spacing(0, 2),
    })
);

const VideoFrame = styled("iframe")(
    ({ theme }) => ({
        border: 0,
        borderRadius: "1.6rem",
        width: "76vw",
        height: "47.5vw",
        overflow: "visible",
        backgroundColor: "#000",
        [theme.breakpoints.up("md")]: {
            width: "40vw",
            height: "25vw",
        },
        [theme.breakpoints.up("xl")]: {
            width: "42vw",
            height: "26.25vw",
        },
    })
);

const logos = [
    { href: "https://www.edgecast.com/", src: "https://www.edgecast.com/assets/images/6126a3eed67d653aa38d06f3-edgecast-logo.svg" },
    // { href: "https://www.stackpath.com/", src: "https://www.stackpath.com/-downloads/stackpath-logo-boxed-screen.svg" },
    { href: "https://www.cloudflare.com/", src: "https://upload.wikimedia.org/wikipedia/commons/4/4b/Cloudflare_Logo.svg" },
    { href: "https://www.fastly.com/", src: "https://upload.wikimedia.org/wikipedia/commons/8/8a/Fastly_logo.svg" },
    { href: "https://www.keycdn.com/", src: "https://logos.keycdn.com/keycdn-logo.svg" },
    { href: "https://www.akamai.com/", src: "https://www.akamai.com/site/en/images/logo/akamai-logo1.svg" },
];

const LogoContainer = (props) => {
    const lg = useMediaQuery("(min-width: 900px)");
    const logoHeight = lg ? 56 : 48;

    return (
        <LogoMarquee>
            {logos.map(({ href, src }) => (
                <LogoLink href={href} key={src}>
                    <img height={logoHeight} src={src} alt="brand logo" />
                </LogoLink>
            ))}
        </LogoMarquee>
    );
};

const BrowserContainer = styled((props) => (
    <Card elevation={0} variant="outlined" {...props} />
))(({ theme }) => ({
    overflow: "hidden",
    width: "76vw",
    fontFamily: "montserrat",
    fontWeight: 600,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    cursor: "default",
    [theme.breakpoints.up("md")]: {
        width: "40vw",
    },
    [theme.breakpoints.up("xl")]: {
        width: "42vw",
    },
}));

const BrowserUI = (props) => {
    return (
        <BrowserContainer>
            <Browser activeTabKey={"paper"} style={{ width: "100%" }}>
                <Tab
                    key={"paper"}
                    title={"Locating CDN Edge Servers with HTTP Responses"}
                >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <img style={{ width: "100%", margin: 0 }} src={process.env.PUBLIC_URL + "/paper.jpeg"} alt="paper content" />
                    </div>
                </Tab>
                <Tab key={"sigcomm"} title={"ACM SIGCOMM 2022"}>
                    <Link
                        href="https://conferences.sigcomm.org/sigcomm/2022/"
                        target="_blank" rel="noreferrer noopener"
                        sx={{
                            display: "flex",
                            transition: "filter 0.3s",
                            "&:hover": {
                                filter: "brightness(64%)",
                            },
                        }}
                    >
                        <img style={{ width: "100%", margin: 0, cursor: "pointer" }} src={process.env.PUBLIC_URL + "/sigcomm.jpeg"} alt="sigcomm website" />
                    </Link>
                </Tab>
            </Browser>
        </BrowserContainer>
    );
};

export default function Homepage(props) {
    return (
        <Fragment>
            <Hero />
            <LazySection sx={{ flexDirection: "row-reverse" }} id="who-deliver">
                <SectionColumn sx={{ justifyContent: "space-around" }}>
                    <Box>
                        <SectionTitle>
                            Who Delivered Your Website?
                        </SectionTitle>
                        <SectionBody>
                            Knowing which CDNs are used in a website is key to web diagnostics.
                            LocEdge can identify over 20 CDN providers, which account for nearly 98% of the market share.
                        </SectionBody>
                    </Box>
                    <LogoContainer />
                    <Typography variant="caption" sx={{
                        fontSize: "0.75rem",
                        color: "#777",
                        marginTop: { xs: 2, md: 0, xl: 2 },
                        marginBottom: { xs: 3.5, md: 0 },
                    }}>
                        * Logos are only used to visually refer to certain CDN providers, and do not indicate any partnership.
                    </Typography>
                </SectionColumn>
                <SectionColumn sx={{
                    alignItems: "center",
                    width: { xs: "100%", md: "45%" },
                    maxWidth: { sm: "74vw" },
                    marginRight: { xs: "auto", md: 0 },
                    transition: "filter 0.3s",
                }}>
                    <PieCard
                        sx={{
                            background: "#f5e1f1",
                            position: "relative",
                            width: { md: "80%", xl: "100%" },
                            "&::after": {
                                content: "'📦'",
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                transform: "translate(36%, 50%)",
                                fontSize: "6.4rem",
                            },
                        }}
                        title="Distributed By"
                        data={{ fastly: 18, google: 11, cloudflare: 8, bunnycdn: 1 }}
                        color={cardColors}
                        icon="🛵"
                    />
                    {/* <Button
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            borderRadius: "10rem",
                            fontFamily: "montserrat",
                            fontWeight: 700,
                            paddingX: 3,
                            paddingY: 2,
                            fontSize: "1.3rem",
                            whiteSpace: "nowrap",
                            opacity: 0,
                        }}
                        variant="contained"
                    >
                        List of Supported Providers
                    </Button> */}
                    <Typography variant="caption" sx={{
                        fontSize: "0.75rem",
                        color: "#777",
                        marginTop: 2,
                        marginBottom: -1,
                    }}>
                        * This is an example result
                        for <Link sx={{ color: "#777" }} href="https://open.spotify.com/">open.spotify.com</Link>
                    </Typography>
                </SectionColumn>
            </LazySection>
            <LazySection id="performance">
                <SectionColumn>
                    <SectionTitle>
                        Towards a Cost-Efficient CDN Solution
                    </SectionTitle>
                    <SectionBody>
                        CDNs with numerous PoPs offer lower latency, but it also comes at the cost of lower cache hit ratios.
                        You should make a tradeoff between minimizing latency and maximizing cache hits.
                        <p>
                            LocEdge can help you find a solution that works best for your website. Start by
                            understanding <Link to="/network" component={RRLink}>how / where</Link> a CDN manages its PoP network and monitor its performance.
                        </p>
                    </SectionBody>
                    <Button
                        variant="outlined"
                        color="secondary"
                        href="https://youtu.be/KJSj_DCQ1bI"
                        target="_blank" rel="noreferrer noopener"
                        sx={{
                            display: { md: "none", xl: "flex" },
                            marginTop: 1,
                            marginBottom: { xs: 4, md: 0 },
                            borderRadius: "2rem",
                            paddingY: 1.5,
                            fontFamily: "montserrat",
                            fontWeight: 600,
                            width: "98%"
                        }}
                    >
                        watch demo
                    </Button>
                </SectionColumn>
                <SectionColumn sx={{ alignItems: { md: "center" }, }}>
                    <VideoFrame src="https://www.youtube.com/embed/KJSj_DCQ1bI" loading="lazy" />
                    <Button
                        variant="outlined"
                        color="secondary"
                        href="https://www.youtube.com/watch?v=KJSj_DCQ1bI"
                        target="_blank" rel="noreferrer noopener"
                        sx={{
                            display: { xs: "none", md: "flex", xl: "none" },
                            borderRadius: "2rem",
                            paddingY: 1.5,
                            fontFamily: "montserrat",
                            fontWeight: 600,
                            width: "92%",
                            marginTop: { md: 0.5, lg: 1.5 },
                        }}
                    >
                        watch demo
                    </Button>
                </SectionColumn>
            </LazySection>
            <LazySection sx={{
                flexDirection: "row-reverse",
                marginTop: { xs: 7, lg: 9 }
            }} id="paper" >
                <SectionColumn>
                    <SectionTitle>
                        Have Your Cake and Eat It Too
                    </SectionTitle>
                    <SectionBody>
                        The physical location of an endpoint can be determined by querying databases like <Link href="https://www.maxmind.com/" target="_blank" rel="noreferrer noopener">Maxmind</Link> or by conducting
                        active measurements. Yet, the former lacks accuracy and the latter is inconvenient to use.
                        <p>
                            LocEdge shows that it is possible to have the benefits of both approaches (<Link component={RRLink} to="/about">Learn More</Link>).
                            Our paper is accepted by SIGCOMM '22 Demo Session.
                            Please refer to this two-page paper for more information.
                        </p>
                    </SectionBody>
                    <Button
                        variant="outlined"
                        disabled
                        sx={{
                            display: { md: "none", xl: "flex" },
                            marginTop: 1,
                            marginBottom: { xs: 4, md: 0 },
                            borderRadius: "2rem",
                            paddingY: 1.5,
                            fontFamily: "montserrat",
                            fontWeight: 600,
                            width: "98%",
                        }}
                    >
                        paper not available for now
                    </Button>
                </SectionColumn>
                <SectionColumn sx={{ alignItems: { md: "center" }, }}>
                    <BrowserUI />
                    <Button
                        variant="outlined"
                        disabled
                        sx={{
                            display: { xs: "none", md: "flex", xl: "none" },
                            borderRadius: "2rem",
                            paddingY: 1.5,
                            fontFamily: "montserrat",
                            fontWeight: 600,
                            width: "92%",
                            marginTop: { md: 1, lg: 2 },
                        }}
                    >
                        paper not available for now
                    </Button>
                </SectionColumn>
            </LazySection>
            <Footer />
        </Fragment>
    );
};